import { evaluateCondition, type ConditionExpression } from '../engine/condition-evaluator';
import type { CategoryExecutor } from './executor.types';

export const executeRulesNode: CategoryExecutor = (node, context, outgoingEdges) => {
  const type = node.nodeType.toLowerCase();
  const config = node.configuration ?? {};

  if (type.endsWith('.if') || type.endsWith('.else')) {
    const conditionExpr =
      typeof config['condition'] === 'string' ? config['condition'] : undefined;
    let passes = false;
    if (conditionExpr) {
      try {
        const result = context.expressions.evaluate(conditionExpr, context.payload);
        passes = Boolean(result);
      } catch {
        passes = false;
      }
    }

    const handle = passes ? 'true' : 'false';
    const byHandle = outgoingEdges.find((e) => e.sourceHandle === handle);
    if (byHandle) return { status: 'success', nextEdgeId: byHandle.id, output: { ruleResult: passes } };

    // Fall back to condition AST on edges
    const branches = [...outgoingEdges].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const matched = branches.find(
      (edge) =>
        !edge.isDefault &&
        edge.condition != null &&
        evaluateCondition(edge.condition as ConditionExpression, context.payload),
    );
    const chosen = matched ?? branches.find((e) => e.isDefault) ?? branches[0];
    return chosen
      ? { status: 'success', nextEdgeId: chosen.id, output: { ruleResult: passes } }
      : { status: 'success', output: { ruleResult: passes } };
  }

  if (type.endsWith('.switch')) {
    const raw = config['cases'];
    // Prefer edge sourceHandle matching evaluated condition result
    let caseValue: unknown;
    if (typeof config['condition'] === 'string') {
      try {
        caseValue = context.expressions.evaluate(config['condition'], context.payload);
      } catch {
        caseValue = undefined;
      }
    }
    const handle = String(caseValue ?? 'default').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const byHandle =
      outgoingEdges.find((e) => e.sourceHandle === handle) ??
      outgoingEdges.find((e) => e.sourceHandle === 'default' || e.isDefault);
    return byHandle
      ? { status: 'success', nextEdgeId: byHandle.id, output: { switchCase: caseValue, cases: raw } }
      : { status: 'success', output: { switchCase: caseValue } };
  }

  // decision-table / score-card / rule-set / *-rule — evaluate optional expression
  if (typeof config['condition'] === 'string') {
    try {
      const result = context.expressions.evaluate(config['condition'], context.payload);
      return { status: 'success', output: { ruleResult: result } };
    } catch (error) {
      return {
        status: 'failure',
        message: error instanceof Error ? error.message : 'Rule evaluation failed',
      };
    }
  }

  return { status: 'success', output: { rulesTable: config['rulesTable'] ?? config['rules'] } };
};
